import { useEffect, useState } from "react";
import { Modal } from "antd";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loadPrivacy, saveAIConsent } from "../store/privacySlice";
import { Button } from "./ui/Button";

type AIConsentDialogProps = { open: boolean; onCancel: () => void; onAccepted: () => void };

export const AIConsentDialog = ({ open, onCancel, onAccepted }: AIConsentDialogProps) => {
  const dispatch = useAppDispatch();
  const { info, loading, saving, error } = useAppSelector((state) => state.privacy);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAgreed(false);
    dispatch(loadPrivacy());
  }, [dispatch, open]);

  useEffect(() => { setAgreed(false); }, [info?.aiDisclosure.version]);

  const handleAccept = async () => {
    if (!agreed || !info || loading || saving) return;
    const result = await dispatch(saveAIConsent({ granted: true, version: info.aiDisclosure.version }));
    if (saveAIConsent.fulfilled.match(result)) onAccepted();
  };

  return (
    <Modal title="Choose whether to share with OpenAI" open={open} onCancel={onCancel} footer={null} closable={!saving} maskClosable={!saving} keyboard={!saving}>
      <div className="space-y-4" data-testid="ai-consent-dialog">
        {loading ? <p role="status">Loading privacy choices…</p> : info && <>
          <p>{info.aiDisclosure.text}</p>
          <p>OpenAI processes data under its policies. See our Privacy Policy for sharing and retention details. You can choose Plan it Myself without agreeing.</p>
          <label className="flex items-start gap-2"><input data-testid="ai-consent-checkbox" type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} disabled={saving} /><span>I agree to share the information described above with OpenAI for AI planning.</span></label>
        </>}
        {error && <div role="alert" className="text-red-700"><p>{error}</p><Button data-testid="ai-consent-retry" variant="ghost" onClick={() => dispatch(loadPrivacy())} disabled={saving || loading}>Retry</Button></div>}
        <nav className="flex gap-4 text-blue-700" aria-label="AI privacy information">
          <a href="https://www.dreamsup.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <a href="https://www.dreamsup.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
        </nav>
        <div className="flex flex-wrap justify-end gap-2">
          <Button data-testid="ai-consent-decline" variant="secondary" onClick={onCancel} disabled={saving}>Not Now</Button>
          <Button data-testid="ai-consent-accept" onClick={handleAccept} disabled={!agreed || !info || loading || !!error} loading={saving}>Allow and Create AI Plan</Button>
        </div>
      </div>
    </Modal>
  );
};
